import { checkQuorum, evaluate, type Evaluation, type Majority, type MajorityBase, type Matter, type QuorumAtClose, type QuorumRule, type Ratio, type RuleSet, type TieRule, type VotingMode } from '@simulador/engine';

// Lógica pura del editor de reglas: frases en lenguaje llano, ediciones que mantienen el RuleSet
// coherente, diferencias entre versiones y la vista previa con el motor. Sin DOM.

/** Mismo patrón que el schema para llaves de mayorías e ids de asuntos. */
export const KEY_PATTERN = /^[a-z][a-z0-9_-]{0,39}$/;

export const RATIO_PRESETS: ReadonlyArray<{ label: string; value: Ratio }> = [
  { label: 'un tercio', value: { num: 1, den: 3 } },
  { label: 'la mitad', value: { num: 1, den: 2 } },
  { label: 'tres quintas partes', value: { num: 3, den: 5 } },
  { label: 'dos tercios', value: { num: 2, den: 3 } },
  { label: 'tres cuartas partes', value: { num: 3, den: 4 } },
  { label: 'la totalidad', value: { num: 1, den: 1 } },
];

export const sameRatio = (a: Ratio, b: Ratio) => a.num * b.den === b.num * a.den;

/** Índice del atajo equivalente (2/4 es la mitad) o -1 si es otra fracción. */
export const presetIndex = (r: Ratio) => RATIO_PRESETS.findIndex((p) => sameRatio(p.value, r));

export function ratioLabel(r: Ratio): string {
  const i = presetIndex(r);
  return i >= 0 ? RATIO_PRESETS[i]!.label : `${r.num}/${r.den}`;
}

export const BASE_LABEL: Record<MajorityBase, string> = {
  emitted: 'de los votos emitidos',
  present: 'de los presentes',
  total: 'del total de integrantes',
};

export const TIE_LABEL: Record<TieRule, string> = {
  casting: 'Voto de calidad de la presidencia',
  repeat: 'Repetir la votación',
  reject: 'Desechar la propuesta',
};

export const MODE_LABEL: Record<VotingMode, string> = {
  nominal: 'Nominal (queda registro por persona)',
  secreta: 'Secreta (sin vínculo entre persona y voto)',
};

export const AT_CLOSE_LABEL: Record<QuorumAtClose, string> = {
  none: 'No revisar',
  present: 'Revisar los presentes',
  ballots: 'Revisar los votos emitidos',
};

const comparison = (strict: boolean) => (strict ? 'más de' : 'al menos');

export function describeMajority(m: Majority): string {
  const abstentions = m.base === 'emitted' ? (m.abstentionsCount ? ', abstenciones incluidas' : ', sin contar abstenciones') : '';
  return `${comparison(m.strict)} ${ratioLabel(m.threshold)} ${BASE_LABEL[m.base]}${abstentions}`;
}

export function describeQuorum(q: QuorumRule): string {
  const open = q.type === 'percent'
    ? `${comparison(q.strict)} ${ratioLabel(q.value)} de los integrantes`
    : `al menos ${q.value} ${q.value === 1 ? 'integrante presente' : 'integrantes presentes'}`;
  const atClose = q.atClose ?? 'none';
  return atClose === 'none' ? `${open}.`
    : atClose === 'present' ? `${open}; al cerrar se revisa otra vez con los presentes.`
    : `${open}; al cerrar, los votos emitidos (abstenciones incluidas) deben alcanzarlo.`;
}

const TIE_SENTENCE: Record<TieRule, string> = {
  casting: 'decide la presidencia con voto de calidad',
  repeat: 'se repite la votación',
  reject: 'la propuesta se desecha',
};

export type RulesSummary = { quorum: string; matters: Array<{ id: string; label: string; text: string }>; tie: string };

export function summarize(rules: RuleSet): RulesSummary {
  return {
    quorum: `Quórum: ${describeQuorum(rules.quorum)}`,
    matters: rules.matters.map((m) => {
      const maj = rules.majorities[m.majority];
      const mode = m.mode && m.mode !== rules.mode ? ` Votación ${m.mode}.` : '';
      return { id: m.id, label: m.label, text: maj ? `se aprueba con ${describeMajority(maj)}.${mode}` : 'sin mayoría asignada.' };
    }),
    tie: `Si hay empate, ${TIE_SENTENCE[rules.tie]}. Votación ${rules.mode} salvo que el tipo de asunto diga otra cosa.`,
  };
}

/** Id válido y libre a partir de un nombre: "Reforma Constitucional" → "reforma-constitucional". */
export function slugify(label: string, taken: ReadonlySet<string>, fallback = 'tipo'): string {
  let base = label.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 32).replace(/-+$/, '');
  if (!/^[a-z]/.test(base)) base = base ? `${fallback}-${base}`.slice(0, 32) : fallback;
  if (!taken.has(base)) return base;
  for (let n = 2; ; n++) if (!taken.has(`${base}-${n}`)) return `${base}-${n}`;
}

/** Versión siguiente a la más alta registrada (no a la base: otra persona pudo registrar una después). */
export function nextFreeVersion(versions: readonly string[]): string {
  let best: [number, number] | null = null;
  for (const v of versions) {
    const m = /^v(\d+)\.(\d+)$/.exec(v);
    if (!m) continue;
    const k: [number, number] = [Number(m[1]), Number(m[2])];
    if (!best || k[0] > best[0] || (k[0] === best[0] && k[1] > best[1])) best = k;
  }
  return best ? `v${best[0]}.${best[1] + 1}` : 'v1.0';
}

// --- Ediciones. Cada una devuelve un RuleSet nuevo; ninguna rompe las referencias asunto → mayoría.

export function addMajority(rules: RuleSet): { rules: RuleSet; key: string } {
  const key = slugify('mayoria', new Set(Object.keys(rules.majorities)));
  const majority: Majority = { label: 'Nueva mayoría', base: 'emitted', threshold: { num: 1, den: 2 }, strict: true, abstentionsCount: false };
  return { rules: { ...rules, majorities: { ...rules.majorities, [key]: majority } }, key };
}

export function updateMajority(rules: RuleSet, key: string, change: Partial<Majority>): RuleSet {
  const current = rules.majorities[key];
  if (!current) return rules;
  return { ...rules, majorities: { ...rules.majorities, [key]: { ...current, ...change } } };
}

/** Tipos de asunto que usan una mayoría: si hay, no se puede quitar. */
export const mattersUsing = (rules: RuleSet, key: string) => rules.matters.filter((m) => m.majority === key);

export function removeMajority(rules: RuleSet, key: string): RuleSet {
  if (mattersUsing(rules, key).length || Object.keys(rules.majorities).length <= 1) return rules;
  const { [key]: _removed, ...rest } = rules.majorities;
  return { ...rules, majorities: rest };
}

export function addMatter(rules: RuleSet, label = 'Nuevo tipo de asunto'): RuleSet {
  const id = slugify(label, new Set(rules.matters.map((m) => m.id)));
  const majority = Object.keys(rules.majorities)[0]!;
  return { ...rules, matters: [...rules.matters, { id, label, majority }] };
}

/**
 * Cambia un tipo de asunto. Si su id no está bloqueado (no viene de una versión anterior), el id
 * sigue al nombre; los bloqueados lo conservan porque los asuntos ya creados lo guardan.
 */
export function updateMatter(rules: RuleSet, index: number, change: { label?: string; majority?: string; mode?: VotingMode | null }, locked: ReadonlySet<string>): RuleSet {
  const current = rules.matters[index];
  if (!current) return rules;
  const next: Matter = { ...current };
  if (change.label !== undefined) {
    next.label = change.label;
    if (!locked.has(current.id)) {
      const taken = new Set(rules.matters.filter((_, i) => i !== index).map((m) => m.id));
      for (const id of locked) taken.add(id);
      next.id = slugify(change.label, taken);
    }
  }
  if (change.majority !== undefined && Object.hasOwn(rules.majorities, change.majority)) next.majority = change.majority;
  if (change.mode !== undefined) {
    if (change.mode === null) delete next.mode;
    else next.mode = change.mode;
  }
  return { ...rules, matters: rules.matters.map((m, i) => (i === index ? next : m)) };
}

export function removeMatter(rules: RuleSet, index: number): RuleSet {
  if (rules.matters.length <= 1) return rules;
  return { ...rules, matters: rules.matters.filter((_, i) => i !== index) };
}

export function moveMatter(rules: RuleSet, index: number, delta: -1 | 1): RuleSet {
  const to = index + delta;
  if (to < 0 || to >= rules.matters.length) return rules;
  const matters = [...rules.matters];
  [matters[index], matters[to]] = [matters[to]!, matters[index]!];
  return { ...rules, matters };
}

/** Cambia el tipo de quórum conservando lo que se pueda. */
export function setQuorumType(q: QuorumRule, type: QuorumRule['type'], members: number): QuorumRule {
  if (q.type === type) return q;
  const atClose = q.atClose ? { atClose: q.atClose } : {};
  return type === 'fixed'
    ? { type: 'fixed', value: Math.max(1, Math.floor(members / 2) + 1), ...atClose }
    : { type: 'percent', value: { num: 1, den: 2 }, strict: true, ...atClose };
}

// --- Diferencias y errores

export function diffRuleSets(prev: RuleSet, next: RuleSet): string[] {
  const out: string[] = [];
  if (describeQuorum(prev.quorum) !== describeQuorum(next.quorum)) out.push(`Quórum: ${describeQuorum(prev.quorum)} → ${describeQuorum(next.quorum)}`);
  if (prev.tie !== next.tie) out.push(`Empate: ${TIE_LABEL[prev.tie]} → ${TIE_LABEL[next.tie]}`);
  if (prev.mode !== next.mode) out.push(`Modalidad: ${prev.mode} → ${next.mode}`);
  for (const [key, m] of Object.entries(next.majorities)) {
    const old = prev.majorities[key];
    if (!old) out.push(`Mayoría nueva: ${m.label} (${describeMajority(m)})`);
    else {
      if (old.label !== m.label) out.push(`Mayoría renombrada: ${old.label} → ${m.label}`);
      if (describeMajority(old) !== describeMajority(m)) out.push(`${m.label}: ${describeMajority(old)} → ${describeMajority(m)}`);
    }
  }
  for (const [key, m] of Object.entries(prev.majorities)) if (!next.majorities[key]) out.push(`Mayoría quitada: ${m.label}`);
  const majorityLabel = (r: RuleSet, key: string) => r.majorities[key]?.label ?? key;
  for (const m of next.matters) {
    const old = prev.matters.find((o) => o.id === m.id);
    if (!old) { out.push(`Tipo de asunto nuevo: ${m.label}`); continue; }
    if (old.label !== m.label) out.push(`Tipo de asunto renombrado: ${old.label} → ${m.label}`);
    if (old.majority !== m.majority) out.push(`${m.label} ahora usa: ${majorityLabel(next, m.majority)}`);
    if ((old.mode ?? null) !== (m.mode ?? null)) out.push(`${m.label}: modalidad ${old.mode ?? 'de la sesión'} → ${m.mode ?? 'de la sesión'}`);
  }
  for (const m of prev.matters) if (!next.matters.some((n) => n.id === m.id)) out.push(`Tipo de asunto quitado: ${m.label}`);
  return out;
}

export type Issue = { path: string; message: string };

/** Errores en un campo o dentro de él (JSON Pointer). */
export const issuesAt = (issues: readonly Issue[], prefix: string) =>
  issues.filter((i) => i.path === prefix || i.path.startsWith(`${prefix}/`));

/** JSON Pointer de una mayoría (escapa ~ y /). */
export const majorityPath = (key: string) => `/majorities/${key.replaceAll('~', '~0').replaceAll('/', '~1')}`;

/** Tipos de asunto de versiones registradas que el borrador ya no tiene. */
export const droppedLocked = (rules: RuleSet, locked: ReadonlySet<string>) =>
  [...locked].filter((id) => !rules.matters.some((m) => m.id === id));

// --- Vista previa

export type PreviewInput = { total: number; present: number; for: number; against: number; abstain: number };
export type Preview =
  | { kind: 'invalid'; message: string }
  | { kind: 'no-quorum'; need: number; message: string }
  | { kind: 'result'; quorumNeed: number; evaluation: Evaluation };

export function preview(rules: RuleSet, matterId: string, input: PreviewInput): Preview {
  const { total, present } = input;
  const ballots = input.for + input.against + input.abstain;
  const nums = [total, present, input.for, input.against, input.abstain];
  if (nums.some((n) => !Number.isSafeInteger(n) || n < 0)) return { kind: 'invalid', message: 'Usa números enteros, sin negativos.' };
  if (present > total) return { kind: 'invalid', message: 'Hay más presentes que integrantes.' };
  if (ballots > present) return { kind: 'invalid', message: 'Hay más votos que presentes.' };
  const quorum = checkQuorum(present, total, rules.quorum);
  if (!quorum.ok) {
    return { kind: 'no-quorum', need: quorum.need, message: `Sin quórum: ${present} de ${quorum.need} presentes necesarios. No se puede abrir la votación.` };
  }
  try {
    const evaluation = evaluate({ for: input.for, against: input.against, abstain: input.abstain }, rules, matterId, { total, present });
    return { kind: 'result', quorumNeed: quorum.need, evaluation };
  } catch (e) {
    return { kind: 'invalid', message: (e as Error).message };
  }
}

/** Nombre legible del campo de un error: "/majorities/simple/threshold" → "Mayoría «Mayoría simple»". */
export function fieldName(path: string, rules: RuleSet | null): string {
  const [, top, key] = path.split('/').map((p) => p.replaceAll('~1', '/').replaceAll('~0', '~'));
  switch (top) {
    case undefined: case '': return 'RuleSet';
    case 'version': return 'Versión';
    case 'quorum': return 'Quórum';
    case 'tie': return 'Empate';
    case 'mode': return 'Modalidad';
    case 'majorities': {
      const label = key !== undefined ? rules?.majorities?.[key]?.label?.trim() : undefined;
      return key === undefined ? 'Mayorías' : `Mayoría «${label || key}»`;
    }
    case 'matters': {
      const label = key !== undefined ? rules?.matters?.[Number(key)]?.label?.trim() : undefined;
      return key === undefined ? 'Tipos de asunto' : `Tipo de asunto «${label || Number(key) + 1}»`;
    }
    default: return top;
  }
}
